// The question curtain's words and cue, shared by apps/game and apps/game-lite.
//
// Before a question opens, a green curtain tells the taster what to do with the
// glass: swirl and sniff for an aroma question, hold it to the light for
// clarity, and so on. The party game had this first (flowCopy.js and
// lessonAction.js); Game Lite shows the same curtain to a solo taster, so the
// pure pieces live here and both apps read one copy. apps/game re-exports them
// from its old paths, so none of its import sites changed.
//
// Pure: no DOM, no React, no assets. Each app maps the action name to its own
// artwork (apps/game/src/lib/lessonActionImages.js, and QuestionCurtain.jsx in
// apps/game-lite).

// ─── Contextual question-transition copy ─────────────────────────────────────

// emoji: 👁️ see  👃 smell  🥂 drink/taste  💭 think/guess  🍷 default
export const QUESTION_CURTAIN_COPY = {
  colour:    { e: '👁️', lines: ["Look closely — what colour is staring back at you?",    "Eyes on the glass — this one's all about the colour"] },
  clarity:   { e: '👁️', lines: ["Hold your glass up to the light",                       "Clear as day, or hazy?"] },
  aroma:     { e: '👃', lines: ["Give it a swirl and get your nose in there",             "Time to take a deep sniff — what do you smell?"] },
  body:      { e: '🥂', lines: ["Focus on the weight — how does it feel in your mouth?",  "Light as water or rich as cream?"] },
  acidity:   { e: '🥂', lines: ["Does your mouth water? That's the acid talking",         "Check the sides of your tongue — any zing?"] },
  sweetness: { e: '🥂', lines: ["Dry or sweet? The tip of your tongue knows first",       "Is there any sugar hiding in there?"] },
  taste:     { e: '🥂', lines: ["What's dancing on your palate right now?",               "Time to taste carefully"] },
  finish:    { e: '🥂', lines: ["Swallow, then count — how long does it linger?",         "The finish tells all — count the seconds"] },
  region:    { e: '💭', lines: ["Where on the map did this wine come from?",               "Can you taste where the grapes were grown?"] },
  vintage:   { e: '💭', lines: ["How old is this bottle?",                                "Can you read the age in the glass?"] },
  price:     { e: '💭', lines: ["Steal or splurge — what's your verdict?",                "Budget buy or premium pour?"] },
  varietal:  { e: '💭', lines: ["Name that grape — if you dare",                          "Can you crack the varietal?"] },
  default:   { e: '🍷', lines: ["Time to put your tasting skills to work",                "Put your nose, eyes, and palate to the test"] },
};

export function classifyQuestion(text) {
  if (!text) return 'default';
  const t = text.toLowerCase();
  if (t.includes('colour') || t.includes('color'))                                                   return 'colour';
  if (t.includes('clear') || t.includes('cloudy') || t.includes('clarity'))                         return 'clarity';
  if (t.includes('smell') || t.includes('aroma') || t.includes('nose'))                             return 'aroma';
  if (t.includes('feel in your mouth') || t.includes('mouthfeel') || t.includes('body'))            return 'body';
  if (t.includes('acidity') || t.includes('acidit'))                                                return 'acidity';
  if (t.includes('sweet'))                                                                           return 'sweetness';
  if (t.includes('what do you taste') || (t.includes('do you taste') && !t.includes('smell')))      return 'taste';
  if (t.includes('how long') || t.includes('flavours stay') || t.includes('flavors stay') || t.includes('aftertaste') || (t.includes('finish') && t.includes('long'))) return 'finish';
  if ((t.includes('region') || (t.includes('where') && t.includes('from'))) && !t.includes('grape')) return 'region';
  if (t.includes('how old') || t.includes('vintage') || (t.includes('age') && t.includes('wine'))) return 'vintage';
  if (t.includes('how much') || t.includes('bottle cost') || (t.includes('cost') && t.includes('wine'))) return 'price';
  if (t.includes('grape variety') || t.includes('grape varietal'))                                  return 'varietal';
  return 'default';
}

// Returns { emoji, heading } — null when questionText is not yet available.
// The line is picked at random from the category's two, so a caller that
// re-renders must hold on to the result rather than call this again.
export function pickQuestionCurtainCopy(questionText) {
  if (!questionText) return null;
  const cat = QUESTION_CURTAIN_COPY[classifyQuestion(questionText)];
  return { emoji: cat.e, heading: cat.lines[Math.floor(Math.random() * cat.lines.length)] };
}

// ─── The action cue beside the copy ──────────────────────────────────────────

// Category → the name of the illustration that shows what to do with the glass.
export const QUESTION_ACTION = {
  colour: 'colour', clarity: 'look', aroma: 'sniff', body: 'taste',
  acidity: 'taste', sweetness: 'taste', taste: 'taste', finish: 'timer',
  region: 'map', vintage: 'vintage', price: 'budget', varietal: 'compare',
  default: 'single',
};

export function questionAction(text) {
  return text ? QUESTION_ACTION[classifyQuestion(text)] : null;
}
